<script>
import { capitalize } from "@/services/helper";
import {
  getMedicalInsuranceNumber,
  getMedicalInsurancePlan,
  getMedicalInsuranceName,
} from "@/services/patient-utils";

export default {
  name: "CardPatient",
  props: {
    patient: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    radioValue: {
      get() {
        return this.isSelected ? this.patient._id : null;
      },
      set(value) {
        if (value === this.patient._id) {
          this.selectPatient();
        }
      },
    },
  },
  methods: {
    getFullName(patient) {
      if (!patient) return "";
      const firstName = capitalize(patient.firstName) || "";
      const lastName = capitalize(patient.lastName) || "";
      return `${firstName} ${lastName}`.trim();
    },

    getMedicalInsurancePlan,
    getMedicalInsuranceNumber,
    getMedicalInsuranceName,

    getGovernmentId(patient) {
      if (!patient.governmentId) {
        return "N/A";
      }
      return patient.governmentId.number || "N/A";
    },

    getAge(patient) {
      if (!patient.bornAt) {
        return "N/A";
      }
      const birthDate = new Date(patient.bornAt);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }

      return age;
    },

    getInitials(patient) {
      if (!patient) return "";

      const firstName = patient.firstName || "";
      const lastName = patient.lastName || "";

      const firstNameInitials = firstName
        .split(" ")
        .filter((name) => name.trim() !== "")
        .map((name) => name.charAt(0).toUpperCase())
        .join("");

      const lastNameInitials = lastName
        .split(" ")
        .filter((name) => name.trim() !== "")
        .map((name) => name.charAt(0).toUpperCase())
        .join("");

      return firstNameInitials + lastNameInitials;
    },

    selectPatient() {
      this.$emit("patient-selected", this.patient);
    },
  },
};
</script>

<template lang="pug">
div.card-patient
  div.firstBox
    div.avatar
      p.avatar-text {{ getInitials(patient) }}
  div.secondBox
    div.box-info-patient
      p.fullName {{ getFullName(patient) }}
      el-radio.radio-grande(v-model="radioValue" :label="patient._id")
    div.box-info-data
      div.box-info
        micon(name="calendar" color="#213572" width="20" height="20")
        p.age {{ getAge(patient) }} años / {{ patient.bornAt | formatDate }}
      div.box-info
        micon(name="dni" color="#213572" width="20" height="20")
        p.government-id {{ getGovernmentId(patient) }}
    div.patient-info-data
      div.patient-info-data-item
        span Número de afiliado:
        p.patient-info-data-item-value {{ getMedicalInsuranceNumber(patient) }}
      div.patient-info-data-item
        span Prepaga:
        p.patient-info-data-item-value {{ getMedicalInsuranceName(patient) }}
      div.patient-info-data-item
        span Plan:
        p.patient-info-data-item-value {{ getMedicalInsurancePlan(patient) }}
    div.patient-info-contact
      p.patient-info-phone-item Teléfono: {{ patient.phone }}
      p.patient-info-email-item Email: {{ patient.email }}
</template>

<style lang="scss" scoped>
.card-patient {
  display: flex;
  background-color: white;
  border-radius: 10px;
  width: 100%;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);

  .firstBox {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    padding: 20px;
    border-right: 2px solid #213572;

    .avatar {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 80px;
      height: 80px;
      border: 2px solid #213572;
      border-radius: 50%;
      background-color: #f8f9fa;

      .avatar-text {
        color: #213572;
        font-size: 1.5rem;
        font-weight: bold;
        line-height: 1;
        text-align: center;
      }
    }
  }

  .secondBox {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 15px;
    padding: 20px;
    min-width: 0;

    .box-info-patient {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .fullName {
        margin: 0;
        font-size: 1.2rem;
        font-weight: bold;
        word-wrap: break-word;
        color: #213572;
      }

      .el-radio :deep(.el-radio__label) {
        display: none;
      }
      .el-radio :deep(.el-radio__inner) {
        width: 20px;
        height: 20px;
        border-color: #5169b1;
        &:hover {
          transform: scale(1.1);
          border-color: #213572;
          transition: all 0.3s ease;
        }
      }
    }

    .age {
      margin: 0;
      font-size: 0.9rem;
    }

    .box-info-data {
      display: flex;
      flex-direction: column;
    }

    .box-info {
      display: flex;
      align-items: center;
      gap: 10px;

      .icon {
        width: 20px;
        height: 20px;
        color: #213572;
      }
    }

    .government-id {
      margin: 0;
      font-size: 0.9rem;
    }

    .patient-info-data {
      display: flex;
      flex-direction: column;
      gap: 5px;

      .patient-info-data-item {
        margin: 0;
        font-size: 0.9rem;
        display: flex;
        align-items: center;
        gap: 5px;

        span {
          font-weight: bold;
        }

        .patient-info-data-item-value {
          margin: 0;
          font-size: 0.9rem;
        }
      }
    }

    .patient-info-contact {
      display: flex;
      gap: 5px;
      margin-top: 10px;

      .patient-info-phone-item {
        margin: 0;
        font-size: 0.9rem;
        background-color: #213572;
        color: white;
        padding: 6px 15px;
        border-radius: 20px;
        font-weight: bold;
        letter-spacing: 0.5px;
        white-space: nowrap;
        flex-shrink: 0;
      }

      .patient-info-email-item {
        margin: 0;
        font-size: 0.9rem;
        border: 2px solid #213572;
        color: #213572;
        padding: 6px 15px;
        border-radius: 20px;
        font-weight: bold;
        letter-spacing: 0.5px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        min-width: 0;
      }
    }
  }
}
</style>
